/**
 * OnboardingUserV2 — Multi-step onboarding wizard (V2 redesign)
 *
 * Steps:
 * 1. Personal data confirmation + password + contribution
 * 2. Extended data (for admins: closing day, agency email, salary file)
 * 3. Company config (for admin representsParticipant)
 * 4. Pensions + flexible retribution
 */
import React, { useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter
} from './ui/dialog';
import {
	Loader2,
	Eye,
	EyeOff,
	CheckCircle,
	XCircle,
	AlertTriangle,
	ChevronRight,
	ChevronLeft,
	Download,
	User,
	Shield,
	Coins,
	FileText
} from 'lucide-react';
import { cn } from '../lib/utils';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import { ArcaContext } from '../context/ArcaContext.context';
import getCurrentUser from '../api/users/getCurrentUser';
import getCompanyInfoEppConfig from '../api/companies/getCompanyInfoEppConfig';
import userCompleteOnboarding from '../api/users/userCompleteOnboarding';
import collectNoWill from '../api/users/collectWillOfNo';
import { CurrentUserI, EmployeeCategory, UserGenderEnum } from '../api/users/interfaces/Users';
import { CompanyWithEppConfigI } from '../api/companies/interfaces/CompanyInfo';
import {
	EmployeeContributionTypeEnum,
	EmployerContributionTypeEnum,
	ContributionPeriodicity
} from '../types/enums';
import { validateNifOrNie } from '../utils/employeeInviteValidation';
import { formatArcaNumber } from '../utils/formatArcaNumbers';
import {
	resolveMatchingContribution,
	resolveMatchingTier
} from '../utils/resolveMatchingContribution';
import { formatArcaDateFromTimestampToYYYYMMDD } from '../utils/formatArcaDates';
import { resolveEmployeeCategory } from '../utils/resolveEmployeeCategory';
import { clearContextAndLocalStorage } from '../utils/clearLocalStorageAndContext';
import PensionSimulatorV2 from './SimulatorsV2/PensionSimulatorV2';
import BotonDescargaEstatica from './BotonDescargaEstatica';
import FileUpload from './FileUpload';
import { UserRole } from '../types/dashboard';

// Step indicator component
const StepIndicator: React.FC<{
	currentStep: number;
	totalSteps: number;
	labels: string[];
}> = ({ currentStep, totalSteps, labels }) => (
	<div className="mb-6">
		<div className="flex justify-between mb-2">
			{labels.map((label, idx) => (
				<div
					key={idx}
					className={cn(
						'text-xs font-medium transition-colors',
						idx + 1 <= currentStep ? 'text-primary' : 'text-muted-foreground'
					)}
				>
					{label}
				</div>
			))}
		</div>
		<Progress value={(currentStep / totalSteps) * 100} className="h-2" />
	</div>
);

const OnboardingUserV2: React.FC = () => {
	const navigate = useNavigate();
	const { token, companyId } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);

	// Loading states
	const [initialLoading, setInitialLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Data states
	const [companyEppInfo, setCompanyEppInfo] = useState<CompanyWithEppConfigI | null>(null);
	const [currentUser, setCurrentUser] = useState<CurrentUserI>();

	// Step state
	const [currentStep, setCurrentStep] = useState(1);

	// Form state - Step 1
	const [userInfo, setUserInfo] = useState({
		name: '',
		firstSurname: '',
		lastSurname: '',
		gender: '',
		nif: '',
		email: '',
		password: '',
		repeatPassword: '',
		birthdate: '',
		documentType: 'NIF',
		employeeCategory: undefined as EmployeeCategory | undefined
	});
	const [monthlyAmount, setMonthlyAmount] = useState<number | ''>(50);
	const [contributionType, setContributionType] = useState<EmployeeContributionTypeEnum>(
		EmployeeContributionTypeEnum.FIXED
	);
	const [showPassword, setShowPassword] = useState(false);
	const [showRepeatPassword, setShowRepeatPassword] = useState(false);
	const [passwordsMatch, setPasswordsMatch] = useState(true);
	const [passwordMeetsRequirements, setPasswordMeetsRequirements] = useState(false);

	// Form state - Step 2 (extended)
	const [closingPayrollDay, setClosingPayrollDay] = useState('');
	const [agencyEmail, setAgencyEmail] = useState('');
	const [grossSalaryFile, setGrossSalaryFile] = useState<File | null>(null);
	const [grossSalaryFileBase64, setGrossSalaryFileBase64] = useState('');

	// Form state - Step 3 (company config for admin)
	const [flexibleCompensationCompanyConfig, setFlexibleCompensationCompanyConfig] = useState({
		hasCompanyHealthInsurance: false,
		companyHealthInsuranceAmount: '' as number | '',
		transportAvailable: false,
		transportAmount: '' as number | '',
		foodAvailable: false,
		foodAmount: '' as number | '',
		childcareAvailable: false,
		childcareAmount: '' as number | '',
		trainingAvailable: false,
		trainingAmount: '' as number | ''
	});

	// Form state - Step 4 (pensions + flex compensation)
	const [hasIndividualPensionPlan, setHasIndividualPensionPlan] = useState<boolean | null>(null);
	const [individualPensionAmount, setIndividualPensionAmount] = useState<number | ''>('');
	const [usesFlexibleCompensation, setUsesFlexibleCompensation] = useState<boolean | null>(null);
	const [flexibleCompensationUserData, setFlexibleCompensationUserData] = useState({
		acceptsCompanyHealthInsurance: false,
		usesTransport: false,
		usesFood: false,
		usesChildcare: false,
		childcareMonthlyAmount: '' as number | '',
		usesTraining: false,
		trainingAnnualAmount: '' as number | ''
	});

	// Admin detection
	const [isAdminRepresentsParticipant, setIsAdminRepresentsParticipant] = useState(false);

	// Dialog states
	const [showResultDialog, setShowResultDialog] = useState(false);
	const [resultMessage, setResultMessage] = useState('');
	const [isError, setIsError] = useState(false);

	const [showSimulatorDialog, setShowSimulatorDialog] = useState(false);
	const [showConfirmCancelDialog, setShowConfirmCancelDialog] = useState(false);

	// Fetch data
	useEffect(() => {
		const fetchData = async () => {
			try {
				const [companyResponse, userResponse] = await Promise.all([
					getCompanyInfoEppConfig(token, companyId),
					getCurrentUser(token)
				]);

				const companyData =
					companyResponse.data?.allCompanies?.find(
						(c: CompanyWithEppConfigI) => c.id === companyId
					) || companyResponse.data?.allCompanies?.[0];
				setCompanyEppInfo(companyData);

				// Set contribution type from the PARTICIPANT's config (employeeContributionType),
				// not the promoter's: the field is the employee's contribution, so its unit
				// (€ for FIXED, % for PERCENTAGE) must follow the participant. Falls back to
				// FIXED when the type is missing.
				setContributionType(
					companyData?.companyEppConfig?.employeeContributionType ??
						EmployeeContributionTypeEnum.FIXED
				);
				// employeeContributionMinimum llega del backend como string (columna numeric
				// serializada) → coercionar a Number para que el <input type="number"> controlado
				// reciba un número y no un string.
				setMonthlyAmount(Number(companyData?.companyEppConfig?.employeeContributionMinimum) || 50);

				if (userResponse.success) {
					const userData = userResponse.data as CurrentUserI;
					setCurrentUser(userData);

					// Detect admin
					const isAdmin = userData?.user?.roles?.includes(UserRole.ADMIN);
					const representsParticipant = userData?.user?.representsParticipant === true;
					setIsAdminRepresentsParticipant(isAdmin && representsParticipant);

					// Populate form
					const { name, firstSurname, lastSurname, gender, nif, email, birthdate } =
						userData?.user || {};
					setUserInfo((prev) => ({
						...prev,
						name: name || '',
						firstSurname: firstSurname || '',
						lastSurname: lastSurname || '',
						gender: gender || '',
						nif: nif || '',
						email: email || '',
						birthdate: birthdate?.replace(/\//g, '-') || '',
						// Las 3 reglas (buzón → plan → representantes) viven en un solo
						// sitio y son las mismas que aplica el backend al aceptar. Ver el
						// docblock de `resolveEmployeeCategory`.
						employeeCategory: resolveEmployeeCategory(userData)
					}));

					// Load flex compensation config if exists
					if (userData?.flexibleCompensationCompanyConfig) {
						const config = userData.flexibleCompensationCompanyConfig;
						setFlexibleCompensationCompanyConfig({
							hasCompanyHealthInsurance: config.hasCompanyHealthInsurance || false,
							companyHealthInsuranceAmount: config.companyHealthInsuranceAmount
								? Number(config.companyHealthInsuranceAmount)
								: '',
							transportAvailable: config.transportAvailable || false,
							transportAmount: config.transportAmount ? Number(config.transportAmount) : '',
							foodAvailable: config.foodAvailable || false,
							foodAmount: config.foodAmount ? Number(config.foodAmount) : '',
							childcareAvailable: config.childcareAvailable || false,
							childcareAmount: config.childcareAmount ? Number(config.childcareAmount) : '',
							trainingAvailable: config.trainingAvailable || false,
							trainingAmount: config.trainingAmount ? Number(config.trainingAmount) : ''
						});
					}
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			} finally {
				setInitialLoading(false);
			}
		};

		fetchData();
	}, [token, companyId]);

	// Validation helpers
	const validatePasswordRequirements = (password: string) => {
		const hasLowerCase = /[a-z]/.test(password);
		const hasUpperCase = /[A-Z]/.test(password);
		const hasNumber = /[0-9]/.test(password);
		const hasMinimumLength = password.length >= 8;
		return {
			isValid: hasLowerCase && hasUpperCase && hasNumber && hasMinimumLength,
			message:
				!hasLowerCase || !hasUpperCase || !hasNumber || !hasMinimumLength
					? translate('onboarding.employee.passwordRequirements')
					: ''
		};
	};

	const validateNifOrNieWithTranslations = (type: string, value: string) => {
		return validateNifOrNie(type, value, {
			nifError: translate('onboarding.employee.nifValidationError'),
			nieError: translate('onboarding.employee.nieValidationError')
		});
	};

	const shouldShowExtendedOnboarding = (): boolean => {
		if (currentUser?.user?.data?.showExtendedOnboarding !== undefined) {
			return currentUser?.user?.data?.showExtendedOnboarding || false;
		}
		return currentUser?.user?.representsParticipant || false;
	};

	const isFormStep1Valid = () => {
		return (
			userInfo.name &&
			userInfo.firstSurname &&
			userInfo.lastSurname &&
			userInfo.nif &&
			userInfo.email &&
			userInfo.password &&
			userInfo.repeatPassword &&
			userInfo.password === userInfo.repeatPassword &&
			passwordMeetsRequirements &&
			userInfo.gender &&
			userInfo.birthdate &&
			monthlyAmount !== ''
		);
	};

	const isFormStep2Valid = () => {
		if (!shouldShowExtendedOnboarding()) return true;
		return closingPayrollDay && grossSalaryFile;
	};

	const isFormStepCompanyConfigValid = () => {
		// All optional - can have no benefits configured
		const checks = [
			flexibleCompensationCompanyConfig.hasCompanyHealthInsurance
				? flexibleCompensationCompanyConfig.companyHealthInsuranceAmount !== ''
				: true,
			flexibleCompensationCompanyConfig.transportAvailable
				? flexibleCompensationCompanyConfig.transportAmount !== ''
				: true,
			flexibleCompensationCompanyConfig.foodAvailable
				? flexibleCompensationCompanyConfig.foodAmount !== ''
				: true,
			flexibleCompensationCompanyConfig.childcareAvailable
				? flexibleCompensationCompanyConfig.childcareAmount !== ''
				: true,
			flexibleCompensationCompanyConfig.trainingAvailable
				? flexibleCompensationCompanyConfig.trainingAmount !== ''
				: true
		];
		return checks.every(Boolean);
	};

	const isFormStepPensionsValid = () => {
		if (hasIndividualPensionPlan === null) return false;
		if (hasIndividualPensionPlan && individualPensionAmount === '') return false;
		if (usesFlexibleCompensation === null) return false;
		if (usesFlexibleCompensation) {
			const hasSelection =
				flexibleCompensationUserData.acceptsCompanyHealthInsurance ||
				flexibleCompensationUserData.usesTransport ||
				flexibleCompensationUserData.usesFood ||
				flexibleCompensationUserData.usesChildcare ||
				flexibleCompensationUserData.usesTraining;
			if (!hasSelection) return false;
			if (
				flexibleCompensationUserData.usesChildcare &&
				flexibleCompensationUserData.childcareMonthlyAmount === ''
			)
				return false;
			if (
				flexibleCompensationUserData.usesTraining &&
				flexibleCompensationUserData.trainingAnnualAmount === ''
			)
				return false;
		}
		return true;
	};

	// Get total steps
	const getTotalSteps = () => {
		let steps = 1; // Step 1 always
		if (shouldShowExtendedOnboarding()) steps++; // Step 2
		if (isAdminRepresentsParticipant) steps++; // Step 3
		steps++; // Final step (pensions)
		return steps;
	};

	const getStepLabels = () => {
		const labels = [translate('onboarding.stepOne.title')];
		if (shouldShowExtendedOnboarding()) labels.push(translate('onboarding.stepTwo.title'));
		if (isAdminRepresentsParticipant)
			labels.push(translate('onboarding.flexibleRetribution.companyConfig.title'));
		labels.push(translate('onboarding.stepPensions.title'));
		return labels;
	};

	// Navigation
	const handleNextStep = () => {
		if (currentStep === 1 && !shouldShowExtendedOnboarding()) {
			// Skip step 2 if not extended
			setCurrentStep(isAdminRepresentsParticipant ? 3 : getTotalSteps());
		} else {
			setCurrentStep((prev) => prev + 1);
		}
	};

	const handlePreviousStep = () => {
		setCurrentStep((prev) => prev - 1);
	};

	const isFinalStep = () => currentStep === getTotalSteps();

	// File handler
	const handleFileChange = async (file: File) => {
		return new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				const base64File = reader.result?.toString() || '';
				if (base64File) {
					const base64Data = base64File.split(',')[1];
					setGrossSalaryFile(file);
					setGrossSalaryFileBase64(base64Data);
					resolve(base64Data);
				} else {
					reject(new Error('Error reading file'));
				}
			};
			reader.onerror = reject;
		});
	};

	// Submit handler
	const handleSubmit = async () => {
		if (isSubmitting) return;

		setIsSubmitting(true);
		try {
			const individualPensionData = {
				hasIndividualPensionPlan,
				individualPensionAmount:
					individualPensionAmount === '' ? 0 : Number(individualPensionAmount)
			};

			const flexCompData = {
				usesFlexibleCompensation,
				...flexibleCompensationUserData,
				childcareMonthlyAmount:
					flexibleCompensationUserData.childcareMonthlyAmount === ''
						? 0
						: Number(flexibleCompensationUserData.childcareMonthlyAmount),
				trainingAnnualAmount:
					flexibleCompensationUserData.trainingAnnualAmount === ''
						? 0
						: Number(flexibleCompensationUserData.trainingAnnualAmount)
			};

			const flexCompCompanyData = isAdminRepresentsParticipant
				? {
						...flexibleCompensationCompanyConfig,
						companyHealthInsuranceAmount:
							flexibleCompensationCompanyConfig.companyHealthInsuranceAmount === ''
								? 0
								: Number(flexibleCompensationCompanyConfig.companyHealthInsuranceAmount),
						transportAmount:
							flexibleCompensationCompanyConfig.transportAmount === ''
								? 0
								: Number(flexibleCompensationCompanyConfig.transportAmount),
						foodAmount:
							flexibleCompensationCompanyConfig.foodAmount === ''
								? 0
								: Number(flexibleCompensationCompanyConfig.foodAmount),
						childcareAmount:
							flexibleCompensationCompanyConfig.childcareAmount === ''
								? 0
								: Number(flexibleCompensationCompanyConfig.childcareAmount),
						trainingAmount:
							flexibleCompensationCompanyConfig.trainingAmount === ''
								? 0
								: Number(flexibleCompensationCompanyConfig.trainingAmount)
					}
				: undefined;

			const response = await userCompleteOnboarding(
				token,
				contributionType,
				monthlyAmount,
				userInfo.gender,
				userInfo.name,
				userInfo.firstSurname,
				userInfo.lastSurname,
				userInfo.nif,
				userInfo.email,
				userInfo.password,
				userInfo.birthdate,
				Number(closingPayrollDay),
				agencyEmail,
				grossSalaryFileBase64,
				individualPensionData,
				flexCompData,
				flexCompCompanyData
			);

			if (response.success) {
				setResultMessage(translate('onboarding.employee.toastSuccessDescription'));
				setIsError(false);
				setShowResultDialog(true);
				setTimeout(() => {
					clearContextAndLocalStorage();
					navigate('/');
				}, 4000);
			} else {
				let message = translate('onboarding.employee.toastFailureDescription');
				if (response?.data === 'USER.NIF_ALREADY_EXISTS') {
					message = translate('onboarding.employee.toastFailureDescriptionNifAlreadyExist');
				}
				setResultMessage(message);
				setIsError(true);
				setShowResultDialog(true);
				setIsSubmitting(false);
			}
		} catch (error) {
			setResultMessage(translate('onboarding.employee.toastFailureDescription'));
			setIsError(true);
			setShowResultDialog(true);
			setIsSubmitting(false);
		}
	};

	// Cancel flow
	const handleCancelClick = () => setShowSimulatorDialog(true);
	const handleConfirmCancel = () => {
		setShowSimulatorDialog(false);
		setShowConfirmCancelDialog(true);
	};
	const handleFinalCancel = async () => {
		const response = await collectNoWill(token);
		if (response.success) {
			clearContextAndLocalStorage();
			navigate('/');
		}
	};

	/** Un importe de cualquier periodicidad, llevado a €/mes. */
	const toMonthlyAmount = (amount: number, periodicity?: ContributionPeriodicity) => {
		switch (periodicity) {
			case ContributionPeriodicity.YEARLY:
				return amount / 12;
			case ContributionPeriodicity.SEMI_ANNUALLY:
				return amount / 6;
			case ContributionPeriodicity.QUARTERLY:
				return amount / 3;
			case ContributionPeriodicity.BI_MONTHLY:
				return amount / 2;
			default:
				return amount;
		}
	};

	/**
	 * Aportación de la empresa en €/mes, para el diálogo que le dice al empleado
	 * cuánto se estaría dejando si rechaza.
	 *
	 * ⚠️ NO delega en `resolveEmployerContribution` a propósito. Se intentó, para
	 * quitar duplicación, y no es equivalente: ese resolver no divide por
	 * `contributionPeriodicity`, así que una empresa con 1.200 €/año pasaba a
	 * anunciar 1.200 €/mes — 12x —, y su fallback de antigüedad calcula un
	 * importe a partir de `minEmployeeContributionPercentage`, que es el mínimo
	 * del EMPLEADO y no una tasa de empresa. Aquí manda mostrar la cifra real o
	 * ninguna; nunca una inventada.
	 */
	const getEmployerContributionAmount = () => {
		const config = companyEppInfo?.companyEppConfig;

		// 1. Lo que el backend ya calculó para ESTE empleado. Es el único camino
		//    por el que resuelven las empresas por tramo salarial y por convenio,
		//    que dejan `employerContributionAmount` a 0.
		//    (sin comprobar `null`: el backend lo declara `number | undefined` y
		//    omite la clave cuando no lo calcula, nunca la manda a null)
		if (currentUser?.calculatedEmployerContribution !== undefined) {
			return Number(currentUser.calculatedEmployerContribution);
		}

		// 2. Tramo por categoría: es la razón por la que se recupera la categoría
		//    del buzón cuando el invitado todavía no tiene plan.
		const categoryTier =
			config?.categoryBracket && userInfo.employeeCategory
				? config.categoryBracket[userInfo.employeeCategory]
				: undefined;
		if (categoryTier) {
			return toMonthlyAmount(categoryTier.contributionAmount, categoryTier.periodicity);
		}

		// 3. Antigüedad: aquí no se calcula (haría falta el tramo del empleado).
		//    Se muestra "no aporta" antes que un número que no está en la config.
		if (config?.seniorityBracket) return 0;

		// 4. Importe uniforme de la empresa, mensualizado.
		return toMonthlyAmount(
			config?.employerContributionAmount || 0,
			config?.contributionPeriodicity
		);
	};

	const monthlyCompanyContributionText = () => {
		// MATCHING mode: the company matches the employee contribution up to the tier cap
		if (companyEppInfo?.companyEppConfig?.matchingBracket) {
			const matchingTier = resolveMatchingTier(
				companyEppInfo.companyEppConfig.matchingBracket,
				userInfo.employeeCategory
			);
			if (matchingTier?.maxAmount !== undefined) {
				return `${translate('user.myContributions.matchingHint')} ${formatArcaNumber(matchingTier.maxAmount)}€/mes`;
			}
			return translate('user.myContributions.matchingHintNoCap');
		}

		const amount = getEmployerContributionAmount();
		if (
			companyEppInfo?.companyEppConfig?.employerContributionType ===
			EmployerContributionTypeEnum.GROSS_SALARY_PERCENTAGE
		) {
			return `${companyEppInfo?.companyEppConfig?.employerContributionAmount}% ${translate('onboarding.employee.salaryPercentageMonthly')}`;
		}
		if (amount === 0) return translate('onboarding.employee.noCompanyContribution');
		return `${formatArcaNumber(amount)}€ ${translate('onboarding.employee.monthly')}`;
	};

	// Loading state
	if (initialLoading) {
		return (
			<div className="min-h-screen bg-linear-to-b from-background to-muted/30 flex items-center justify-center">
				<Loader2 className="w-12 h-12 text-primary animate-spin" />
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-linear-to-b from-background to-muted/30 py-8 px-4">
			<div className="max-w-2xl mx-auto">
				{/* Header */}
				<div className="text-center mb-6">
					<img src="/imgs/LOGO_VERDE_2.svg" alt="ARCA" className="h-10 mx-auto mb-4" />
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						{translate('onboarding.employee.welcome')}{' '}
						{currentUser?.user?.name || currentUser?.user?.email}
					</h1>
					<p className="text-muted-foreground mt-2">
						{translate('onboarding.employee.intro')}{' '}
						<span className="font-semibold text-foreground">{companyEppInfo?.name}</span>{' '}
						{translate('onboarding.employee.intro2')}
					</p>
				</div>

				{/* Benefits summary */}
				<Card className="mb-6 border-primary/20 bg-primary/5">
					<CardContent className="p-4">
						<h3 className="font-semibold text-foreground mb-2">
							{translate('onboarding.employee.why')}
						</h3>
						<ul className="space-y-1.5 text-sm text-muted-foreground">
							<li className="flex items-start gap-2">
								<CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
								{currentUser?.user?.roles?.includes(UserRole.ADMIN)
									? translate('onboarding.employee.why2Admin')
									: translate('onboarding.employee.why2')}
							</li>
							<li className="flex items-start gap-2">
								<CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
								{translate('onboarding.employee.why3')}
							</li>
							<li className="flex items-start gap-2">
								<CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
								{translate('onboarding.employee.why4')}
							</li>
						</ul>
					</CardContent>
				</Card>

				{/* Step indicator */}
				<StepIndicator
					currentStep={currentStep}
					totalSteps={getTotalSteps()}
					labels={getStepLabels()}
				/>

				{/* Form Card */}
				<Card className="border-none shadow-xl">
					<CardContent className="p-6">
						<AnimatePresence mode="wait">
							{/* Step 1: Personal Data */}
							{currentStep === 1 && (
								<motion.div
									key="step1"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									className="space-y-4"
								>
									<div className="flex items-center gap-2 mb-4">
										<User className="w-5 h-5 text-primary" />
										<h2 className="text-lg font-semibold">
											{translate('onboarding.stepOne.title')}
										</h2>
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<Label>{translate('onboarding.employee.inputName')}</Label>
											<Input
												value={userInfo.name}
												onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
												className={cn(!userInfo.name && 'border-destructive')}
											/>
										</div>
										<div>
											<Label>{translate('onboarding.employee.inputFirstSurname')}</Label>
											<Input
												value={userInfo.firstSurname}
												onChange={(e) => setUserInfo({ ...userInfo, firstSurname: e.target.value })}
												className={cn(!userInfo.firstSurname && 'border-destructive')}
											/>
										</div>
										<div>
											<Label>{translate('onboarding.employee.inputLastSurname')}</Label>
											<Input
												value={userInfo.lastSurname}
												onChange={(e) => setUserInfo({ ...userInfo, lastSurname: e.target.value })}
											/>
										</div>
										<div>
											<Label>{translate('onboarding.employee.inputBirthDate')}</Label>
											<Input
												type="date"
												value={userInfo.birthdate}
												onChange={(e) => setUserInfo({ ...userInfo, birthdate: e.target.value })}
												max={(() => {
													const d = new Date();
													d.setFullYear(d.getFullYear() - 18);
													return formatArcaDateFromTimestampToYYYYMMDD(d.toISOString());
												})()}
												className={cn(!userInfo.birthdate && 'border-destructive')}
											/>
										</div>
									</div>

									{/* Gender */}
									<div>
										<Label>{translate('onboarding.employee.inputGender')}</Label>
										<RadioGroup
											value={userInfo.gender}
											onValueChange={(val) => setUserInfo({ ...userInfo, gender: val })}
											className="flex gap-4 mt-2"
										>
											<div className="flex items-center gap-2">
												<RadioGroupItem value={UserGenderEnum.MEN} id="male" />
												<Label htmlFor="male" className="cursor-pointer">
													{translate('onboarding.employee.inputGender1')}
												</Label>
											</div>
											<div className="flex items-center gap-2">
												<RadioGroupItem value={UserGenderEnum.WOMEN} id="female" />
												<Label htmlFor="female" className="cursor-pointer">
													{translate('onboarding.employee.inputGender2')}
												</Label>
											</div>
										</RadioGroup>
									</div>

									{/* Document */}
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<Label>{translate('onboarding.employee.inputDocumentType')}</Label>
											<RadioGroup
												value={userInfo.documentType}
												onValueChange={(val) => setUserInfo({ ...userInfo, documentType: val })}
												className="flex gap-4 mt-2"
											>
												<div className="flex items-center gap-2">
													<RadioGroupItem value="NIF" id="nif" />
													<Label htmlFor="nif">NIF</Label>
												</div>
												<div className="flex items-center gap-2">
													<RadioGroupItem value="NIE" id="nie" />
													<Label htmlFor="nie">NIE</Label>
												</div>
											</RadioGroup>
											<Input
												value={userInfo.nif}
												onChange={(e) =>
													setUserInfo({ ...userInfo, nif: e.target.value.toUpperCase() })
												}
												className={cn(
													'mt-2',
													userInfo.nif &&
														!validateNifOrNieWithTranslations(userInfo.documentType, userInfo.nif)
															.isValid &&
														'border-destructive'
												)}
											/>
											{userInfo.nif &&
												!validateNifOrNieWithTranslations(userInfo.documentType, userInfo.nif)
													.isValid && (
													<p className="text-xs text-destructive mt-1">
														{
															validateNifOrNieWithTranslations(userInfo.documentType, userInfo.nif)
																.message
														}
													</p>
												)}
										</div>
										<div>
											<Label>{translate('onboarding.employee.inputEmail')}</Label>
											<Input value={userInfo.email} disabled className="bg-muted/50 mt-2" />
										</div>
									</div>

									{/* Password */}
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<Label>{translate('onboarding.employee.inputNewPassword')}</Label>
											<div className="relative mt-2">
												<Input
													type={showPassword ? 'text' : 'password'}
													value={userInfo.password}
													onChange={(e) => {
														setUserInfo({ ...userInfo, password: e.target.value });
														setPasswordMeetsRequirements(
															validatePasswordRequirements(e.target.value).isValid
														);
														setPasswordsMatch(
															e.target.value === userInfo.repeatPassword || !userInfo.repeatPassword
														);
													}}
													className={cn(
														!passwordMeetsRequirements && userInfo.password && 'border-destructive'
													)}
												/>
												<button
													type="button"
													onClick={() => setShowPassword(!showPassword)}
													className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
												>
													{showPassword ? (
														<EyeOff className="w-4 h-4" />
													) : (
														<Eye className="w-4 h-4" />
													)}
												</button>
											</div>
											{userInfo.password && !passwordMeetsRequirements && (
												<p className="text-xs text-destructive mt-1">
													{validatePasswordRequirements(userInfo.password).message}
												</p>
											)}
										</div>
										<div>
											<Label>{translate('onboarding.employee.placeholderRepeatNewPassword')}</Label>
											<div className="relative mt-2">
												<Input
													type={showRepeatPassword ? 'text' : 'password'}
													value={userInfo.repeatPassword}
													onChange={(e) => {
														setUserInfo({ ...userInfo, repeatPassword: e.target.value });
														setPasswordsMatch(
															e.target.value === userInfo.password || !e.target.value
														);
													}}
													className={cn(
														!passwordsMatch && userInfo.repeatPassword && 'border-destructive'
													)}
												/>
												<button
													type="button"
													onClick={() => setShowRepeatPassword(!showRepeatPassword)}
													className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
												>
													{showRepeatPassword ? (
														<EyeOff className="w-4 h-4" />
													) : (
														<Eye className="w-4 h-4" />
													)}
												</button>
											</div>
											{!passwordsMatch && userInfo.repeatPassword && (
												<p className="text-xs text-destructive mt-1">
													{translate('onboarding.employee.passwordsDontMatch')}
												</p>
											)}
										</div>
									</div>

									{/* Contribution */}
									<div className="pt-4 border-t">
										<Label className="text-base font-semibold">
											{translate('user.myContributions.modalSelectContributionType')}
										</Label>
										<div className="flex items-center gap-4 mt-3">
											<div className="relative flex-1 min-w-32">
												<Input
													type="number"
													value={monthlyAmount}
													onChange={(e) =>
														setMonthlyAmount(e.target.value === '' ? '' : Number(e.target.value))
													}
													onFocus={(e) => e.target.select()}
													min={
														Number(companyEppInfo?.companyEppConfig?.employeeContributionMinimum) ||
														1
													}
													max={contributionType === EmployeeContributionTypeEnum.FIXED ? 9999 : 100}
													className="pr-10"
												/>
												<span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
													{contributionType === EmployeeContributionTypeEnum.FIXED ? '€' : '%'}
												</span>
											</div>
											<span className="text-sm text-muted-foreground">
												{translate('user.myContributions.modalMonthlyContributionAdvice')}
											</span>
										</div>
										{/* MATCHING mode: live preview of the matched company contribution */}
										{companyEppInfo?.companyEppConfig?.matchingBracket &&
											contributionType === EmployeeContributionTypeEnum.FIXED && (
												<p className="text-sm text-muted-foreground mt-2">
													{translate('user.myContributions.matchingHint')}{' '}
													<span className="font-semibold text-foreground">
														{formatArcaNumber(
															resolveMatchingTier(
																companyEppInfo.companyEppConfig.matchingBracket,
																userInfo.employeeCategory
															)?.maxAmount ?? 0
														)}
														€/mes
													</span>
													{' — '}
													{translate('user.myContributions.companyContributes')}
													{': '}
													<span className="font-semibold text-primary">
														{formatArcaNumber(
															resolveMatchingContribution({
																matchingBracket: companyEppInfo.companyEppConfig.matchingBracket,
																employeeCategory: userInfo.employeeCategory,
																employeeMonthlyContribution: Number(monthlyAmount || 0)
															})
														)}
														€/mes
													</span>
												</p>
											)}
									</div>
								</motion.div>
							)}

							{/* Step 2: Extended Data */}
							{currentStep === 2 && shouldShowExtendedOnboarding() && (
								<motion.div
									key="step2"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									className="space-y-4"
								>
									<div className="flex items-center gap-2 mb-4">
										<FileText className="w-5 h-5 text-primary" />
										<h2 className="text-lg font-semibold">
											{translate('onboarding.stepTwo.title')}
										</h2>
									</div>

									<div>
										<Label>1. {translate('onboarding.employee.inputClosingPayrollDay')}</Label>
										<Input
											type="number"
											min={1}
											max={31}
											placeholder="20"
											value={closingPayrollDay}
											onChange={(e) => setClosingPayrollDay(e.target.value)}
											className={cn('mt-2', !closingPayrollDay && 'border-destructive')}
										/>
									</div>

									<div>
										<Label>2. {translate('onboarding.employee.inputAgencyEmail')}</Label>
										<Input
											type="email"
											value={agencyEmail}
											onChange={(e) => setAgencyEmail(e.target.value)}
											placeholder={translate('onboarding.employee.agencyEmailPlaceholder')}
											className="mt-2"
										/>
									</div>

									<div>
										<Label>3. {translate('onboarding.employee.inputGrossSalaryFile')}</Label>
										<p className="text-xs text-muted-foreground mb-2">
											{translate('onboarding.employee.inputGrossSalaryFile2')}
										</p>
										<div className="flex items-center gap-3">
											<FileUpload
												onFileUpload={handleFileChange}
												borderColor={grossSalaryFile ? '#10B981' : '#EF4444'}
											/>
											<BotonDescargaEstatica
												ruta="/documents/Plantilla_Empleados_Simple.csv"
												nombre="Plantilla_Empleados_Simple.csv"
												className="gap-1.5"
											>
												<Download className="w-4 h-4" />
												{translate('onboarding.employee.inputDownloadTemplate')}
											</BotonDescargaEstatica>
										</div>
										{grossSalaryFile && (
											<p className="text-xs text-primary mt-2">
												<CheckCircle className="w-3 h-3 inline mr-1" />
												{grossSalaryFile.name}
											</p>
										)}
									</div>
								</motion.div>
							)}

							{/* Step 3: Company Config (Admin only) */}
							{currentStep === 3 && isAdminRepresentsParticipant && (
								<motion.div
									key="step3"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									className="space-y-4"
								>
									<div className="flex items-center gap-2 mb-4">
										<Coins className="w-5 h-5 text-primary" />
										<h2 className="text-lg font-semibold">
											{translate('onboarding.flexibleRetribution.companyConfig.title')}
										</h2>
									</div>
									<p className="text-sm text-muted-foreground mb-4">
										{translate('onboarding.flexibleRetribution.companyConfig.subtitle')}
									</p>

									{/* Benefits checkboxes with amounts */}
									<div className="space-y-3">
										{/* Health Insurance */}
										{/* La fila entera es el control: el `<label>` envuelve casilla y texto, así que el
										    objetivo es toda la banda y no una casilla de 16 px con un área de 44 colgando
										    que se solapaba con la fila siguiente. El campo de importe va FUERA del label:
										    dentro, pulsarlo alternaría la casilla. */}
										<div className="rounded-lg transition-colors hover:bg-muted/50">
											<label className="flex cursor-pointer items-start gap-3 p-3">
												<Checkbox
													checked={flexibleCompensationCompanyConfig.hasCompanyHealthInsurance}
													onCheckedChange={(checked) =>
														setFlexibleCompensationCompanyConfig({
															...flexibleCompensationCompanyConfig,
															hasCompanyHealthInsurance: !!checked
														})
													}
												/>
												<span className="flex-1 text-sm leading-5">
													{translate(
														'onboarding.flexibleRetribution.companyConfig.healthInsurance'
													)}
												</span>
											</label>
											{flexibleCompensationCompanyConfig.hasCompanyHealthInsurance && (
												<Input
													type="number"
													placeholder="150"
													value={flexibleCompensationCompanyConfig.companyHealthInsuranceAmount}
													onChange={(e) =>
														setFlexibleCompensationCompanyConfig({
															...flexibleCompensationCompanyConfig,
															companyHealthInsuranceAmount:
																e.target.value === '' ? '' : Number(e.target.value)
														})
													}
													className="mt-2 w-40"
												/>
											)}
										</div>

										{/* Transport */}
										{/* La fila entera es el control: el `<label>` envuelve casilla y texto, así que el
										    objetivo es toda la banda y no una casilla de 16 px con un área de 44 colgando
										    que se solapaba con la fila siguiente. El campo de importe va FUERA del label:
										    dentro, pulsarlo alternaría la casilla. */}
										<div className="rounded-lg transition-colors hover:bg-muted/50">
											<label className="flex cursor-pointer items-start gap-3 p-3">
												<Checkbox
													checked={flexibleCompensationCompanyConfig.transportAvailable}
													onCheckedChange={(checked) =>
														setFlexibleCompensationCompanyConfig({
															...flexibleCompensationCompanyConfig,
															transportAvailable: !!checked
														})
													}
												/>
												<span className="flex-1 text-sm leading-5">
													{translate('onboarding.flexibleRetribution.companyConfig.transport')}
												</span>
											</label>
											{flexibleCompensationCompanyConfig.transportAvailable && (
												<Input
													type="number"
													placeholder="100"
													value={flexibleCompensationCompanyConfig.transportAmount}
													onChange={(e) =>
														setFlexibleCompensationCompanyConfig({
															...flexibleCompensationCompanyConfig,
															transportAmount: e.target.value === '' ? '' : Number(e.target.value)
														})
													}
													className="mt-2 w-40"
												/>
											)}
										</div>

										{/* Food */}
										{/* La fila entera es el control: el `<label>` envuelve casilla y texto, así que el
										    objetivo es toda la banda y no una casilla de 16 px con un área de 44 colgando
										    que se solapaba con la fila siguiente. El campo de importe va FUERA del label:
										    dentro, pulsarlo alternaría la casilla. */}
										<div className="rounded-lg transition-colors hover:bg-muted/50">
											<label className="flex cursor-pointer items-start gap-3 p-3">
												<Checkbox
													checked={flexibleCompensationCompanyConfig.foodAvailable}
													onCheckedChange={(checked) =>
														setFlexibleCompensationCompanyConfig({
															...flexibleCompensationCompanyConfig,
															foodAvailable: !!checked
														})
													}
												/>
												<span className="flex-1 text-sm leading-5">
													{translate('onboarding.flexibleRetribution.companyConfig.food')}
												</span>
											</label>
											{flexibleCompensationCompanyConfig.foodAvailable && (
												<Input
													type="number"
													placeholder="100"
													value={flexibleCompensationCompanyConfig.foodAmount}
													onChange={(e) =>
														setFlexibleCompensationCompanyConfig({
															...flexibleCompensationCompanyConfig,
															foodAmount: e.target.value === '' ? '' : Number(e.target.value)
														})
													}
													className="mt-2 w-40"
												/>
											)}
										</div>
									</div>
								</motion.div>
							)}

							{/* Step 4 (or 3 for non-admin): Pensions */}
							{isFinalStep() && (
								<motion.div
									key="stepFinal"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									className="space-y-6"
								>
									<div className="flex items-center gap-2 mb-4">
										<Shield className="w-5 h-5 text-primary" />
										<h2 className="text-lg font-semibold">
											{translate('onboarding.stepPensions.title')}
										</h2>
									</div>

									{/* Individual pension question */}
									<div>
										<Label className="text-base">
											{translate('onboarding.stepPensions.question1')}
										</Label>
										<RadioGroup
											value={
												hasIndividualPensionPlan === null ? '' : String(hasIndividualPensionPlan)
											}
											onValueChange={(val) => {
												setHasIndividualPensionPlan(val === 'true');
												if (val === 'false') setIndividualPensionAmount('');
											}}
											className="flex gap-4 mt-2"
										>
											<div className="flex items-center gap-2">
												<RadioGroupItem value="true" id="pension-yes" />
												<Label htmlFor="pension-yes">{translate('general.yes')}</Label>
											</div>
											<div className="flex items-center gap-2">
												<RadioGroupItem value="false" id="pension-no" />
												<Label htmlFor="pension-no">{translate('general.no')}</Label>
											</div>
										</RadioGroup>

										{hasIndividualPensionPlan && (
											<div className="mt-3">
												<Label>{translate('onboarding.stepPensions.question2')}</Label>
												<Input
													type="number"
													value={individualPensionAmount}
													onChange={(e) =>
														setIndividualPensionAmount(
															e.target.value === '' ? '' : Number(e.target.value)
														)
													}
													placeholder="0"
													className="mt-2 w-40"
												/>
												<p className="text-xs text-muted-foreground mt-1">
													{translate('onboarding.stepPensions.amountHelp')}
												</p>
											</div>
										)}
									</div>

									{/* Flexible compensation question */}
									<div className="pt-4 border-t">
										<Label className="text-base">
											{translate('onboarding.flexibleRetribution.userSelection.question')}
										</Label>
										<RadioGroup
											value={
												usesFlexibleCompensation === null ? '' : String(usesFlexibleCompensation)
											}
											onValueChange={(val) => {
												setUsesFlexibleCompensation(val === 'true');
												if (val === 'false') {
													setFlexibleCompensationUserData({
														acceptsCompanyHealthInsurance: false,
														usesTransport: false,
														usesFood: false,
														usesChildcare: false,
														childcareMonthlyAmount: '',
														usesTraining: false,
														trainingAnnualAmount: ''
													});
												}
											}}
											className="flex gap-4 mt-2"
										>
											<div className="flex items-center gap-2">
												<RadioGroupItem value="true" id="flex-yes" />
												<Label htmlFor="flex-yes">{translate('general.yes')}</Label>
											</div>
											<div className="flex items-center gap-2">
												<RadioGroupItem value="false" id="flex-no" />
												<Label htmlFor="flex-no">{translate('general.no')}</Label>
											</div>
										</RadioGroup>

										{usesFlexibleCompensation && (
											<div className="mt-4 space-y-3">
												<p className="text-sm font-medium">
													{translate(
														'onboarding.flexibleRetribution.userSelection.benefitsQuestion'
													)}
												</p>

												{flexibleCompensationCompanyConfig.hasCompanyHealthInsurance && (
													<label className="flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/50">
														<Checkbox
															checked={flexibleCompensationUserData.acceptsCompanyHealthInsurance}
															onCheckedChange={(checked) =>
																setFlexibleCompensationUserData({
																	...flexibleCompensationUserData,
																	acceptsCompanyHealthInsurance: !!checked
																})
															}
														/>
														<span className="flex-1 text-sm leading-5">
															{translate(
																'onboarding.flexibleRetribution.userSelection.healthInsurance'
															)}{' '}
															({flexibleCompensationCompanyConfig.companyHealthInsuranceAmount}
															€/mes)
														</span>
													</label>
												)}

												{flexibleCompensationCompanyConfig.transportAvailable && (
													<label className="flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/50">
														<Checkbox
															checked={flexibleCompensationUserData.usesTransport}
															onCheckedChange={(checked) =>
																setFlexibleCompensationUserData({
																	...flexibleCompensationUserData,
																	usesTransport: !!checked
																})
															}
														/>
														<span className="flex-1 text-sm leading-5">
															{translate('onboarding.flexibleRetribution.userSelection.transport')}
														</span>
													</label>
												)}

												{flexibleCompensationCompanyConfig.foodAvailable && (
													<label className="flex cursor-pointer items-center gap-3 rounded-lg p-3 transition-colors hover:bg-muted/50">
														<Checkbox
															checked={flexibleCompensationUserData.usesFood}
															onCheckedChange={(checked) =>
																setFlexibleCompensationUserData({
																	...flexibleCompensationUserData,
																	usesFood: !!checked
																})
															}
														/>
														<span className="flex-1 text-sm leading-5">
															{translate('onboarding.flexibleRetribution.userSelection.food')}
														</span>
													</label>
												)}
											</div>
										)}
									</div>
								</motion.div>
							)}
						</AnimatePresence>

						{/* Navigation. Con `flex-wrap`: en el último paso son tres botones y en un teléfono no
						    caben en una fila («Confirmo» se salía por la derecha en un iPhone de 402 px). */}
						<div className="flex flex-wrap justify-between gap-2 mt-8 pt-4 border-t">
							<div>
								{currentStep > 1 && (
									<Button variant="outline" onClick={handlePreviousStep} className="gap-1">
										<ChevronLeft className="w-4 h-4" />
										{translate('general.previous')}
									</Button>
								)}
							</div>
							<div className="flex flex-wrap justify-end gap-2">
								<Button
									variant="outline"
									onClick={handleCancelClick}
									className="border-destructive text-destructive hover:bg-destructive/10"
								>
									{translate('onboarding.employee.rejection')}
								</Button>
								{isFinalStep() ? (
									<Button
										onClick={handleSubmit}
										disabled={isSubmitting || !isFormStepPensionsValid()}
										className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
									>
										{isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
										{translate('onboarding.employee.modalAmountConfirmation')}
									</Button>
								) : (
									<Button
										onClick={handleNextStep}
										disabled={
											(currentStep === 1 && !isFormStep1Valid()) ||
											(currentStep === 2 && !isFormStep2Valid()) ||
											(currentStep === 3 &&
												isAdminRepresentsParticipant &&
												!isFormStepCompanyConfigValid())
										}
										className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1"
									>
										{translate('general.next')}
										<ChevronRight className="w-4 h-4" />
									</Button>
								)}
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Result Dialog */}
			<Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<div className="flex items-center gap-3">
							{isError ? (
								<div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
									<XCircle className="w-5 h-5 text-destructive" />
								</div>
							) : (
								<div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
									<CheckCircle className="w-5 h-5 text-primary" />
								</div>
							)}
							<DialogTitle>
								{isError
									? translate('onboarding.employee.toastFailureTitle')
									: translate('onboarding.employee.toastSuccessTitle')}
							</DialogTitle>
						</div>
					</DialogHeader>
					<DialogDescription>{resultMessage}</DialogDescription>
				</DialogContent>
			</Dialog>

			{/* Simulator Dialog */}
			<Dialog open={showSimulatorDialog} onOpenChange={setShowSimulatorDialog}>
				<DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>{translate('onboarding.simulator.title')}</DialogTitle>
					</DialogHeader>
					<PensionSimulatorV2 currentAge={0} />
					<div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 my-4">
						<div className="flex items-start gap-3">
							<AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
							<p className="text-sm">
								{translate('onboarding.employee.areYouSureMessage')}{' '}
								<span className="font-semibold">{monthlyCompanyContributionText()}</span>{' '}
								<span className="font-semibold">
									{translate('onboarding.employee.areYouSureMessage2')}
								</span>
							</p>
						</div>
					</div>
					<DialogFooter className="gap-2">
						<Button
							onClick={() => setShowSimulatorDialog(false)}
							className="rounded-full bg-primary"
						>
							{translate('general.genericModal.closeThinkAgain')}
						</Button>
						<Button
							variant="outline"
							onClick={handleConfirmCancel}
							className="rounded-full border-destructive text-destructive"
						>
							{translate('general.genericModal.confirmCancelAdhesion')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Confirm Cancel Dialog */}
			<Dialog open={showConfirmCancelDialog} onOpenChange={setShowConfirmCancelDialog}>
				<DialogContent>
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
								<AlertTriangle className="w-5 h-5 text-destructive" />
							</div>
							<DialogTitle>{translate('onboarding.employee.areYouSure')}</DialogTitle>
						</div>
					</DialogHeader>
					<DialogDescription>{translate('onboarding.employee.cancelMessage')}</DialogDescription>
					<DialogFooter className="gap-2">
						<Button
							onClick={() => setShowConfirmCancelDialog(false)}
							className="rounded-full bg-primary"
						>
							{translate('general.genericModal.closeThinkAgain')}
						</Button>
						<Button
							variant="outline"
							onClick={handleFinalCancel}
							className="rounded-full border-destructive text-destructive"
						>
							{translate('general.genericModal.confirmCancelAdhesion')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default OnboardingUserV2;
